import React from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute - Wrapper component to protect routes based on permissions
 * 
 * Usage:
 * <ProtectedRoute screen="scheduler" action="view">
 *   <Scheduler />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ children, screen, action = 'view', requireAll = false }) => {
  const { isAuthenticated, loading, hasPermission, user } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} sx={{ color: '#E4C984' }} />
        <Typography variant="h6" sx={{ color: '#666' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Not authenticated - this shouldn't happen if Login is properly set up
  // but keeping as failsafe
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            maxWidth: 400
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: '#E4C984', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Authentication Required
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Please log in to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Check permissions if screen and action are provided
  if (screen && action) {
    const actions = Array.isArray(action) ? action : [action];
    
    let hasAccess;
    if (requireAll) {
      // User must have ALL specified permissions
      hasAccess = actions.every(a => hasPermission(screen, a));
    } else {
      // User must have AT LEAST ONE permission
      hasAccess = actions.some(a => hasPermission(screen, a));
    }

    if (!hasAccess) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              maxWidth: 500
            }}
          >
            <LockIcon sx={{ fontSize: 64, color: '#999', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Access Denied
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              You don't have permission to access this page.
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              {user?.email && `Logged in as: ${user.email}`}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
              Required: {screen}:{Array.isArray(action) ? action.join(' or ') : action}
            </Typography>
          </Paper>
        </Box>
      );
    }
  }

  // User is authenticated and has permission
  return children;
};

export default ProtectedRoute;

